
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, ArrowRight, Send, CheckCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { sendContactForm } from "@/utils/emailService";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

// Modified schema to make website URL validation less strict
const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  website: z.string().optional(), // No URL validation here, allowing simple entries
  message: z.string().min(5, "Message must be at least 5 characters"),
  service: z.string().default("SEO Consultation"),
});

type FormData = z.infer<typeof formSchema>;

interface ContactFormProps {
  neighborhoodName?: string;
  onSuccess?: () => void;
}

const ContactForm = ({ neighborhoodName, onSuccess }: ContactFormProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [partialLeadCaptured, setPartialLeadCaptured] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    website: "",
    message: neighborhoodName 
      ? `I'm interested in SEO services for my business in ${neighborhoodName}.` 
      : "",
    service: "SEO Consultation",
  });

  const { toast } = useToast();
  const navigate = useNavigate();
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: formData,
  });
  
  const capturePartialLead = async () => {
    if (partialLeadCaptured) return;

    try {
      const { error: dbError } = await supabase.from("leads").insert({
        name: form.getValues("name"),
        email: form.getValues("email"),
        phone: form.getValues("phone") || null,
        message: "Partial lead from contact form (step 1)",
        service: form.getValues("service"),
        source: "contact_partial",
        is_partial: true,
      });

      if (dbError) {
        console.error("Error capturing partial lead:", dbError);
      } else {
        setPartialLeadCaptured(true);
      }
    } catch (error) {
      console.error("Error in capturePartialLead:", error);
    }
  };

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);

    try {
      const contactData = {
        name: data.name,
        email: data.email,
        phone: data.phone || "",
        website: data.website || "",
        message: data.message,
        service: data.service,
      };

      const success = await sendContactForm(contactData);
      
      if (success) {
        toast({
          title: "Message sent successfully",
          description: "We'll be in touch with you shortly.",
        });
        
        form.reset();
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        } else {
          // Always set session storage to indicate form was submitted
          sessionStorage.setItem("formSubmitted", "true");
          
          // Always redirect to thank you page for consistent user experience
          navigate("/thank-you");
        }
      } else {
        toast({
          title: "Error sending message",
          description: "Please try again later.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      toast({
        title: "Error sending message",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Multi-step form logic
  const nextStep = async () => {
    const isFirstStepValid = await form.trigger(['name', 'email']);
    if (isFirstStepValid) {
      setFormData({
        ...formData,
        name: form.getValues('name'),
        email: form.getValues('email'),
      });
      
      // Capture partial lead when moving to step 2
      await capturePartialLead();
      
      setCurrentStep(2);
    }
  };

  const prevStep = () => {
    setCurrentStep(1);
  };

  // Animation variants
  const formVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { 
        duration: 0.5,
        ease: "easeOut"
      } 
    },
    exit: { 
      opacity: 0, 
      x: 20,
      transition: { 
        duration: 0.3,
        ease: "easeIn"
      } 
    }
  };

  const formFieldVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5,
        delay: i * 0.1,
        ease: "easeOut"
      } 
    })
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {currentStep === 1 ? (
          <motion.div
            variants={formVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="space-y-4"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-desert-sunset text-white font-semibold text-sm">1</div>
                <h3 className="ml-2 font-medium">Your Information</h3>
              </div>
              <div className="text-xs text-muted-foreground">Step 1 of 2</div>
            </div>

            <motion.div custom={0} variants={formFieldVariants} initial="hidden" animate="visible">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          placeholder="Your Name *" 
                          className="pl-4 pr-4 py-6 text-base bg-gradient-to-r from-background to-muted/30 focus:from-background focus:to-background border-desert-sunset/20 focus:border-desert-sunset transition-all rounded-xl" 
                          {...field} 
                        />
                        <div className="absolute left-0 top-0 h-full w-1 bg-desert-sunset/20 rounded-l-xl transition-all group-focus-within:bg-desert-sunset/50" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>
            
            <motion.div custom={1} variants={formFieldVariants} initial="hidden" animate="visible">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          placeholder="Email Address *" 
                          type="email" 
                          className="pl-4 pr-4 py-6 text-base bg-gradient-to-r from-background to-muted/30 focus:from-background focus:to-background border-desert-sky/20 focus:border-desert-sky transition-all rounded-xl" 
                          {...field} 
                        />
                        <div className="absolute left-0 top-0 h-full w-1 bg-desert-sky/20 rounded-l-xl transition-all group-focus-within:bg-desert-sky/50" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>
            
            <motion.div custom={2} variants={formFieldVariants} initial="hidden" animate="visible">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          placeholder="Phone Number (optional)" 
                          className="pl-4 pr-4 py-6 text-base bg-gradient-to-r from-background to-muted/30 focus:from-background focus:to-background border-desert-sky/20 focus:border-desert-sky transition-all rounded-xl" 
                          {...field} 
                        />
                        <div className="absolute left-0 top-0 h-full w-1 bg-desert-sky/20 rounded-l-xl transition-all group-focus-within:bg-desert-sky/50" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>
            
            <motion.div 
              className="flex justify-end mt-6"
              custom={3} 
              variants={formFieldVariants} 
              initial="hidden" 
              animate="visible"
            >
              <Button
                type="button"
                onClick={nextStep}
                className="group bg-gradient-to-r from-desert-sunset to-desert-canyon hover:from-desert-canyon hover:to-desert-sunset text-white transition-all px-6 py-2 rounded-xl"
              >
                <span>Continue</span>
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            variants={formVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="space-y-4"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-desert-sky text-white font-semibold text-sm">2</div>
                <h3 className="ml-2 font-medium">Project Details</h3>
              </div>
              <div className="text-xs text-muted-foreground">Step 2 of 2</div>
            </div>

            <motion.div custom={0} variants={formFieldVariants} initial="hidden" animate="visible">
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          placeholder="Website (optional)" 
                          className="pl-4 pr-4 py-6 text-base bg-gradient-to-r from-background to-muted/30 focus:from-background focus:to-background border-desert-cactus/20 focus:border-desert-cactus transition-all rounded-xl" 
                          {...field} 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>
            
            <motion.div custom={1} variants={formFieldVariants} initial="hidden" animate="visible">
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative group">
                        <Textarea 
                          placeholder="Your Message *" 
                          className="min-h-[150px] pl-4 pr-4 py-4 text-base bg-gradient-to-r from-background to-muted/30 focus:from-background focus:to-background border-desert-canyon/20 focus:border-desert-canyon transition-all rounded-xl resize-none" 
                          {...field} 
                        />
                        <div className="absolute left-0 top-0 h-full w-1 bg-desert-canyon/20 rounded-l-xl transition-all group-focus-within:bg-desert-canyon/50" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>
            
            <FormField
              control={form.control}
              name="service"
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input type="hidden" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <motion.div 
              className="flex justify-between mt-6"
              custom={2} 
              variants={formFieldVariants} 
              initial="hidden" 
              animate="visible"
            >
              <Button
                type="button"
                onClick={prevStep}
                variant="outline"
                className="border-desert-sunset/20 text-desert-sunset hover:bg-desert-sunset/5 transition-all"
              >
                Back
              </Button>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button 
                        type="submit" 
                        className="group bg-gradient-to-r from-desert-sky to-desert-cactus hover:from-desert-cactus hover:to-desert-sky text-white font-medium transition-all px-6 py-2 rounded-xl shadow-md hover:shadow-lg" 
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <span className="flex items-center">
                              <span>Request Free Consultation</span>
                              <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                            </span>
                          </>
                        )}
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">Get a free SEO analysis {neighborhoodName ? `for your ${neighborhoodName} business` : "for your business"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </motion.div>
          </motion.div>
        )}
        
        <div className="flex items-center pt-4 border-t border-muted mt-6">
          <motion.div 
            className="flex items-center text-xs text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <CheckCircle className="h-3 w-3 mr-1 text-desert-cactus" />
            <span>Your data is secure and will never be shared with third parties</span>
          </motion.div>
        </div>
      </form>
    </Form>
  );
};

export default ContactForm;
